import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { CheckCircle, Calendar, User, Eye, Search, X, Clock, DollarSign, FileText, Image, AlertCircle, RotateCcw, History, Paperclip, Trash2, Loader2, Pencil, Save } from 'lucide-react';
import ProjectHistoryModal from '@/components/ProjectHistoryModal';
import { canUserEdit, canUserDelete } from '@/utils/permissions';

const PAGE_PATH = '/projects/completed';

const emptyEditForm = {
  name: '',
  description: '',
  projectIncharge: '',
  startDate: '',
  deadline: '',
  completedDate: '',
  estimatedCost: '',
  actualCost: '',
  remarks: '',
};

function CompletedProjects() {
  const { user, API } = useContext(AppContext);
  const canEdit = canUserEdit(user, PAGE_PATH);
  const canDelete = canUserDelete(user, PAGE_PATH);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProject, setHistoryProject] = useState(null);
  const [docDeletingId, setDocDeletingId] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const openEdit = (p) => {
    setEditingProject(p);
    setEditForm({
      name: p.name || '',
      description: p.description || '',
      projectIncharge: p.projectIncharge || '',
      startDate: p.startDate || '',
      deadline: p.deadline || '',
      completedDate: p.completedDate || '',
      estimatedCost: p.estimatedCost ?? '',
      actualCost: p.actualCost ?? '',
      remarks: p.remarks || '',
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingProject) return;
    if (!editForm.name.trim()) { alert('Project name is required.'); return; }
    setSavingEdit(true);
    try {
      await axios.put(`${API}/projects/${editingProject.id}`, {
        name: editForm.name.trim(),
        description: editForm.description,
        projectIncharge: editForm.projectIncharge,
        startDate: editForm.startDate,
        deadline: editForm.deadline,
        completedDate: editForm.completedDate,
        estimatedCost: parseFloat(editForm.estimatedCost) || 0,
        actualCost: parseFloat(editForm.actualCost) || 0,
        remarks: editForm.remarks,
      });
      setShowEditModal(false);
      setEditingProject(null);
      // refetch
      const res = await axios.get(`${API}/projects/completed`);
      setProjects(res.data);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (p) => {
    if (!canDelete) return;
    if (!window.confirm(`Permanently delete completed project "${p.name}"? This will remove all its documents and history. This cannot be undone.`)) return;
    setDeletingId(p.id);
    try {
      await axios.delete(`${API}/projects/${p.id}`);
      setProjects((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete project');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteDoc = async (projectId, doc) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete document "${doc.name || doc.filename}"?`)) return;
    setDocDeletingId(doc.id);
    try {
      await axios.delete(`${API}/projects/${projectId}/documents/${doc.id}`);
      setProjects((prev) => prev.map((p) =>
        p.id === projectId ? { ...p, documents: (p.documents || []).filter((d) => d.id !== doc.id) } : p
      ));
      if (selectedProject?.id === projectId) {
        setSelectedProject((p) => p ? { ...p, documents: (p.documents || []).filter((d) => d.id !== doc.id) } : p);
      }
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete document');
    } finally {
      setDocDeletingId(null);
    }
  };

  const formatSize = (b) => {
    if (!b && b !== 0) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects/completed`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReopen = async (project) => {
    if (!window.confirm(`Re-open "${project.name}"? It will move back to the Ongoing Projects page.`)) return;
    setReopeningId(project.id);
    try {
      await axios.post(`${API}/projects/${project.id}/reopen`);
      // Optimistic remove from UI
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to re-open project');
    } finally {
      setReopeningId(null);
    }
  };

  const viewProjectDetails = (project) => {
    setSelectedProject(project);
    setShowDetailModal(true);
  };

  const filteredProjects = projects.filter(p =>
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.projectIncharge?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="completed-projects-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Completed Projects</h1>
          <p className="text-gray-500 mt-1">Archive of successfully completed projects</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none w-64"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center space-x-2">
            <CheckCircle className="text-green-500" size={18} />
            <p className="text-sm text-green-600 font-medium">Total Completed</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{projects.length}</p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Total Investment</p>
          <p className="text-2xl font-bold text-gray-900">
            {formatCurrency(projects.reduce((sum, p) => sum + (p.actualCost || 0), 0))}
          </p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Budget Saved</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(projects.reduce((sum, p) => sum + ((p.estimatedCost || 0) - (p.actualCost || 0)), 0))}
          </p>
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-500">Avg. Completion Time</p>
          <p className="text-2xl font-bold text-gray-900">6 Months</p>
        </div>
      </div>

      {/* Project Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow">
            <div className="p-5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-green-600 bg-green-50 px-2 py-1 rounded">{project.id}</span>
                <span className="flex items-center space-x-1 text-xs text-green-600">
                  <CheckCircle size={14} />
                  <span>Completed</span>
                </span>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mt-3">{project.name}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{project.description}</p>

              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center space-x-1">
                    <User size={14} />
                    <span>Incharge</span>
                  </span>
                  <span className="text-gray-900 font-medium">{project.projectIncharge}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500 flex items-center space-x-1">
                    <Calendar size={14} />
                    <span>Completed</span>
                  </span>
                  <span className="text-gray-900 font-medium">{project.completedDate}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Budget</span>
                  <span className="text-gray-900 font-medium">{formatCurrency(project.actualCost)}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => viewProjectDetails(project)}
                  className="flex-1 text-sm text-[#1a237e] hover:text-[#283593] font-medium flex items-center justify-center space-x-1 py-2 rounded-lg hover:bg-[#1a237e]/5 transition"
                  data-testid={`view-details-${project.id}`}
                >
                  <Eye size={14} />
                  <span>View Full Details</span>
                </button>
                <button
                  onClick={() => { setHistoryProject(project); setHistoryOpen(true); }}
                  className="text-sm text-indigo-700 hover:text-indigo-900 font-medium flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-indigo-50 hover:bg-indigo-100 transition"
                  title="Project Timeline"
                  data-testid={`history-completed-${project.id}`}
                >
                  <History size={14} />
                  <span>Timeline</span>
                </button>
                {canEdit && (
                  <button
                    onClick={() => openEdit(project)}
                    className="text-sm text-blue-700 hover:text-blue-900 font-medium flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition"
                    title="Edit"
                    data-testid={`edit-completed-${project.id}`}
                  >
                    <Pencil size={14} />
                    <span>Edit</span>
                  </button>
                )}
                <button
                  onClick={() => handleReopen(project)}
                  disabled={reopeningId === project.id}
                  className="text-sm text-amber-700 hover:text-amber-900 font-medium flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 transition disabled:opacity-60"
                  title="Move back to Ongoing"
                  data-testid={`reopen-project-${project.id}`}
                >
                  <RotateCcw size={14} />
                  <span>{reopeningId === project.id ? 'Re-opening…' : 'Re-open'}</span>
                </button>
                {canDelete && (
                  <button
                    onClick={() => handleDelete(project)}
                    disabled={deletingId === project.id}
                    className="text-sm text-rose-700 hover:text-rose-900 font-medium flex items-center justify-center gap-1 px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 transition disabled:opacity-60"
                    title="Delete project"
                    data-testid={`delete-completed-${project.id}`}
                  >
                    {deletingId === project.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <CheckCircle className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-gray-500">No completed projects found</p>
        </div>
      )}

      {/* Project Detail Modal */}
      {showDetailModal && selectedProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white rounded-t-2xl">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 bg-white/20 rounded text-xs font-mono">{selectedProject.id}</span>
                    <span className="px-2 py-1 bg-white/20 rounded text-xs flex items-center gap-1">
                      <CheckCircle size={12} /> Completed
                    </span>
                  </div>
                  <h2 className="text-xl font-bold">{selectedProject.name}</h2>
                  <p className="text-white/80 text-sm mt-1">{selectedProject.description}</p>
                </div>
                <button 
                  onClick={() => setShowDetailModal(false)} 
                  className="p-2 hover:bg-white/20 rounded-lg transition"
                  data-testid="close-modal-btn"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Key Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <User size={14} />
                    <span>Project Incharge</span>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedProject.projectIncharge}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar size={14} />
                    <span>Start Date</span>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedProject.startDate}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <Calendar size={14} />
                    <span>Deadline</span>
                  </div>
                  <p className="font-semibold text-gray-900">{selectedProject.deadline}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-600 text-sm mb-1">
                    <CheckCircle size={14} />
                    <span>Completed On</span>
                  </div>
                  <p className="font-semibold text-green-700">{selectedProject.completedDate}</p>
                </div>
              </div>

              {/* Budget Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign size={18} className="text-[#1a237e]" />
                  Budget Details
                </h3>
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Estimated Cost</p>
                      <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedProject.estimatedCost)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Actual Cost</p>
                      <p className="text-xl font-bold text-[#1a237e]">{formatCurrency(selectedProject.actualCost)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Savings</p>
                      <p className={`text-xl font-bold ${(selectedProject.estimatedCost - selectedProject.actualCost) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(selectedProject.estimatedCost - selectedProject.actualCost)}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Budget Utilization</span>
                      <span className="font-medium">{((selectedProject.actualCost / selectedProject.estimatedCost) * 100).toFixed(1)}%</span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          (selectedProject.actualCost / selectedProject.estimatedCost) <= 1 ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((selectedProject.actualCost / selectedProject.estimatedCost) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Phases */}
              {selectedProject.phases && selectedProject.phases.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock size={18} className="text-[#1a237e]" />
                    Project Phases
                  </h3>
                  <div className="space-y-3">
                    {selectedProject.phases.map((phase, index) => (
                      <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                          phase.status === 'Completed' ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          {phase.status === 'Completed' ? (
                            <CheckCircle size={16} className="text-white" />
                          ) : (
                            <span className="text-white text-sm font-bold">{index + 1}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{phase.name}</p>
                          <p className="text-sm text-gray-500">{phase.status}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            phase.progress === 100 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {phase.progress}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents */}
              {selectedProject.documents && selectedProject.documents.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Paperclip size={18} className="text-[#1a237e]" />
                    Documents ({selectedProject.documents.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedProject.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                        <a href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`} target="_blank" rel="noreferrer"
                           className="flex items-center gap-2 text-sm text-blue-700 hover:underline truncate flex-1">
                          <FileText size={14} className="shrink-0" />
                          <span className="truncate">{doc.name}</span>
                        </a>
                        <span className="text-xs text-gray-400 mx-3 shrink-0">{formatSize(doc.size)}</span>
                        <span className="text-xs text-gray-500 mx-3 shrink-0 hidden sm:block">{doc.uploadedBy || ''}</span>
                        {canDelete && (
                          <button onClick={() => handleDeleteDoc(selectedProject.id, doc)}
                                  disabled={docDeletingId === doc.id}
                                  className="text-rose-600 hover:bg-rose-50 p-1.5 rounded disabled:opacity-50"
                                  data-testid={`delete-doc-completed-${doc.id}`}>
                            {docDeletingId === doc.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Documents & Drawings */}
              {selectedProject.drawings && selectedProject.drawings.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText size={18} className="text-[#1a237e]" />
                    Documents & Drawings
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProject.drawings.map((doc, index) => (
                      <div key={index} className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm">
                        <FileText size={14} />
                        <span>{doc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Images */}
              {selectedProject.images && selectedProject.images.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Image size={18} className="text-[#1a237e]" />
                    Project Images
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    {selectedProject.images.map((img, index) => (
                      <div key={index} className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <img src={img} alt={`Project ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedProject.remarks && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <AlertCircle size={18} className="text-[#1a237e]" />
                    Remarks
                  </h3>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800">{selectedProject.remarks}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Edit Completed Project Modal */}
      {showEditModal && editingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-completed-modal">
          <form onSubmit={handleSaveEdit} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-green-600 to-emerald-600 rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold text-white">Edit Completed Project</h2>
                <p className="text-xs text-white/80">{editingProject.name}</p>
              </div>
              <button type="button" onClick={() => setShowEditModal(false)} className="p-2 text-white/80 hover:bg-white/10 rounded-lg"><X size={20} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Project Name *</label>
                <input type="text" required value={editForm.name}
                       onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                       className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
                       data-testid="edit-completed-name-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea rows="2" value={editForm.description}
                          onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Project In-charge</label>
                  <input type="text" value={editForm.projectIncharge}
                         onChange={(e) => setEditForm((p) => ({ ...p, projectIncharge: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Completed On</label>
                  <input type="date" value={editForm.completedDate ? editForm.completedDate.slice(0, 10) : ''}
                         onChange={(e) => setEditForm((p) => ({ ...p, completedDate: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Date</label>
                  <input type="date" value={editForm.startDate}
                         onChange={(e) => setEditForm((p) => ({ ...p, startDate: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Original Deadline</label>
                  <input type="date" value={editForm.deadline}
                         onChange={(e) => setEditForm((p) => ({ ...p, deadline: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-gray-700">Estimated Cost (₹)</label>
                  <input type="number" step="0.01" value={editForm.estimatedCost}
                         onChange={(e) => setEditForm((p) => ({ ...p, estimatedCost: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Actual Cost (₹)</label>
                  <input type="number" step="0.01" value={editForm.actualCost}
                         onChange={(e) => setEditForm((p) => ({ ...p, actualCost: e.target.value }))}
                         className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Remarks</label>
                <textarea rows="2" value={editForm.remarks}
                          onChange={(e) => setEditForm((p) => ({ ...p, remarks: e.target.value }))}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg" />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-2xl">
              <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button type="submit" disabled={savingEdit}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-60"
                      data-testid="edit-completed-save-btn">
                <Save size={16} /> {savingEdit ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Project History Timeline */}
      <ProjectHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        projectId={historyProject?.id}
        projectName={historyProject?.name}
      />
    </div>
  );
}

export default CompletedProjects;
